/**
 * Deterministic game signals — the analytical heart of the insight engine.
 *
 * Pure functions over the parsed `AtBatDoc[]` play-by-play. These compute the HARD
 * facts (fatigue cliff, command-collapse, first-pitch-strike, two-strike putaway,
 * leadoff outcomes) so the AI narration layer *narrates real numbers* rather than
 * inventing them. Sprint 1 scope: the pitching command/fatigue family + batting
 * standouts. Everything here is parser-independent and unit-testable.
 *
 * Voice note: these signals are inputs, not copy. All coach-facing wording is
 * generated downstream in the suggestive "bench coach" voice — never here.
 */

import { AtBatDoc } from '../../types/dugout';
import { StatsSheetPitcher } from '../../types/import.types';
import { PitcherRates } from '../pdf/reconcileGameImport';

// The signal interfaces live in the shared prompt module (api/_lib/insights/) so
// the Vercel functions can use them without importing from src/. Re-exported here
// so existing importers of this engine keep working unchanged.
import type {
  PitcherByInning,
  PitcherGameSignal,
  BatterGameSignal,
  GameSignals,
} from '../../../api/_lib/insights/game-insight-prompt';
export type { PitcherByInning, PitcherGameSignal, BatterGameSignal, GameSignals };

const HIT_CODES = new Set(['1B', '2B', '3B', 'HR']);
const isWalk = (ab: AtBatDoc) => ab.resultCode === 'BB';
const isHbp = (ab: AtBatDoc) => ab.resultCode === 'HBP';
const isK = (ab: AtBatDoc) => ab.resultCode === 'K';
const isHit = (ab: AtBatDoc) => HIT_CODES.has(ab.resultCode);
/** An official at-bat excludes walks, HBP, and sacrifices. */
const isAB = (ab: AtBatDoc) => !isWalk(ab) && !isHbp(ab) && ab.resultCode !== 'SAC' && ab.resultCode !== 'SF';

function pct(n: number, d: number): number {
  return d > 0 ? n / d : 0;
}

/** Highest-confidence key for grouping a player across PAs (id if matched, else name). */
function keyOf(id: string | undefined, name: string | undefined): string {
  return id || (name || 'Unknown').trim().toLowerCase();
}

/** Reconstruct cumulative-strike progression to detect a two-strike count was reached. */
function reachedTwoStrikes(ab: AtBatDoc): boolean {
  let s = 0;
  for (const p of ab.pitchSequence) {
    if (p === 'S' || p.startsWith('S')) {
      s++;
      if (s >= 2) return true;
    }
  }
  return (ab.strikes ?? 0) >= 2;
}

/**
 * Per-pitcher signals for OUR pitchers only (pitcherTeam === 'us').
 *
 * `exactLines` is the Game Stats Sheet's per-pitcher line (reconcileGameImport's
 * `pitcherLines`), keyed by playerId, when a box score was uploaded alongside the
 * scorebook. It is GameChanger's own computed BF/pitches/strikes/H/BB/SO — the
 * scorebook's cell-by-cell grid parse is only ever an estimate of those same
 * numbers (it can misattribute an at-bat to the wrong pitcher at a relief
 * boundary), so the exact line wins whenever one exists for that pitcher.
 * Everything the exact line doesn't cover (fatigue cliff, command-collapse,
 * two-strike putaway, by-inning, leadoff) still comes from the play-by-play,
 * since only the scorebook has that detail at all.
 */
export function computePitcherSignals(
  atBats: AtBatDoc[],
  exactLines: Record<string, StatsSheetPitcher> = {},
  rateLines: Record<string, PitcherRates> = {},
): PitcherGameSignal[] {
  const ours = atBats.filter(ab => ab.pitcherTeam === 'us' && ab.confidence !== 'low');
  const groups = new Map<string, AtBatDoc[]>();
  for (const ab of ours) {
    const k = keyOf(ab.pitcherId, ab.pitcherName);
    (groups.get(k) ?? groups.set(k, []).get(k)!).push(ab);
  }

  // A pitcher can have an exact stats-sheet line yet NO at-bats grouped to them:
  // the scorebook's roster grid only records a pitching change in the position
  // columns, so a fielder who came in to pitch is invisible to it. Seed an empty
  // group per known pitcher so they still get a card, built from the exact line.
  for (const pid of Object.keys(exactLines)) if (!groups.has(pid)) groups.set(pid, []);

  const result: PitcherGameSignal[] = [];
  for (const [groupKey, pas] of groups) {
    // chronological: by inning, preserving parse order within an inning
    const ordered = [...pas].sort((a, b) => a.inning - b.inning);
    const pid = ordered[0]?.pitcherId ?? groupKey;
    const exact = exactLines[pid];
    const rates = rateLines[pid];

    const battersFaced = exact?.bf ?? ordered.length;
    const pitchCount = exact?.pitches ?? ordered.reduce((s, ab) => s + (ab.pitchCount || ab.pitchSequence.length), 0);
    const strikes = exact?.strikes ?? ordered.reduce((s, ab) => s + (ab.strikes ?? 0), 0);
    const walks = exact?.bb ?? ordered.filter(isWalk).length;
    const hbp = ordered.filter(isHbp).length;
    const strikeouts = exact?.so ?? ordered.filter(isK).length;
    const hitsAllowed = exact?.h ?? ordered.filter(isHit).length;

    const withPitches = ordered.filter(ab => ab.pitchSequence.length > 0);
    const fpsHits = withPitches.filter(ab => {
      const p0 = ab.pitchSequence[0];
      return p0 === 'S' || p0?.startsWith('S');
    }).length;
    // GC's own FPS% first (the CSV measured it directly), then the scorebook's
    // pitch sequences, then null. Never 0 as a stand-in: a pitcher who came only
    // from the stats sheet has no first-pitch data at all, and reporting 0% reads
    // as "never threw a first-pitch strike" — a damning claim about a kid,
    // invented from missing data. Note a REAL 0% (Henry Cooper, 0 of 4 here) is a
    // different thing, and only the CSV can tell the two apart.
    const csvFps = rates?.fpsPct != null ? (rates.fpsPct > 1 ? rates.fpsPct / 100 : rates.fpsPct) : null;
    const firstPitchStrikePct = csvFps ?? (withPitches.length > 0 ? pct(fpsHits, withPitches.length) : null);
    const strikePct = pct(strikes, pitchCount);

    // command-collapse: ahead but can't finish. Needs real pitch sequences.
    const walkRate = pct(walks + hbp, battersFaced);
    const commandCollapse = {
      detected: firstPitchStrikePct != null && firstPitchStrikePct >= 0.55 && walkRate >= 0.18 && battersFaced >= 4,
      fpsPct: firstPitchStrikePct ?? 0,
      walkRate,
    };

    // fatigue cliff: split the outing's pitches in half chronologically, compare strike%
    const fatigue = computeFatigue(ordered);

    // two-strike putaway
    const reached2 = ordered.filter(reachedTwoStrikes);
    const putaways = reached2.filter(isK).length;
    const twoStrikePutaway = {
      reachedTwoStrikes: reached2.length,
      putaways,
      rate: pct(putaways, reached2.length),
    };

    // leadoff
    const leadoffPAs = ordered.filter(ab => ab.isLeadoff);
    const leadoffReached = leadoffPAs.filter(ab => ab.reachedBase);
    const runsByInning = new Map<number, number>();
    for (const ab of ordered) runsByInning.set(ab.inning, (runsByInning.get(ab.inning) ?? 0) + (ab.runnersScored || 0));
    const inningsScoredAfterReach = leadoffReached.filter(ab => (runsByInning.get(ab.inning) ?? 0) > 0).length;

    // per-inning breakdown — only kept when the totals reconcile with the overall
    // pitch count. When pitcherByInning assigns an entire inning to the wrong pitcher
    // (mid-inning relief edge case), the by-inning sub-totals won't add up; suppressing
    // them prevents the AI narrating a fabricated "inning A vs inning B" story.
    const rawByInning = buildByInning(ordered);
    const byInningPitches = rawByInning.reduce((s, i) => s + i.pitches, 0);
    const byInningWalks = rawByInning.reduce((s, i) => s + i.walks, 0);
    const byInningConsistent =
      rawByInning.length >= 2 &&
      Math.abs(byInningPitches - pitchCount) <= 3 &&
      byInningWalks === walks;
    const byInning = byInningConsistent ? rawByInning : [];

    const firstName = ordered.find(ab => ab.pitcherName)?.pitcherName;
    const firstNum = ordered.find(ab => ab.pitcherNumber)?.pitcherNumber;
    result.push({
      pitcherId: ordered[0]?.pitcherId ?? (exact ? groupKey : undefined),
      pitcherName: firstName || exact?.name || (firstNum ? `#${firstNum}` : 'Unknown'),
      battersFaced,
      pitchCount,
      strikes,
      strikePct,
      firstPitchStrikePct,
      walks,
      hbp,
      strikeouts,
      hitsAllowed,
      commandCollapse,
      fatigue,
      twoStrikePutaway,
      leadoff: { faced: leadoffPAs.length, reached: leadoffReached.length, inningsScoredAfterReach },
      byInning,
      rates: rates
        ? {
            fpsoPct: rates.fpsoPct != null ? rates.fpsoPct / 100 : undefined,
            fpswPct: rates.fpswPct != null ? rates.fpswPct / 100 : undefined,
            fpshPct: rates.fpshPct != null ? rates.fpshPct / 100 : undefined,
            smPct:   rates.smPct   != null ? rates.smPct   / 100 : undefined,
            weakPct: rates.weakPct != null ? rates.weakPct / 100 : undefined,
            kBf: rates.kBf, loo: rates.loo, lowPitchInn: rates.lowPitchInn,
            goAo: rates.goAo, baa: rates.baa,
          }
        : undefined,
    });
  }
  return result;
}

function computeFatigue(ordered: AtBatDoc[]): PitcherGameSignal['fatigue'] {
  // Flatten pitches in order, tagging each as strike(1)/ball(0).
  const pitches: number[] = [];
  for (const ab of ordered) {
    for (const p of ab.pitchSequence) pitches.push(p === 'S' || p.startsWith('S') ? 1 : 0);
  }
  const total = pitches.length;
  if (total < 25) {
    return { earlyStrikePct: 0, lateStrikePct: 0, dropPct: 0, cliffPitch: null, detected: false };
  }
  const mid = Math.floor(total / 2);
  const early = pitches.slice(0, mid);
  const late = pitches.slice(mid);
  const earlyStrikePct = pct(early.reduce((a, b) => a + b, 0), early.length);
  const lateStrikePct = pct(late.reduce((a, b) => a + b, 0), late.length);
  const dropPct = earlyStrikePct - lateStrikePct;

  // cliff: first pitch index where a 10-pitch rolling strike% drops >=15pts below the early rate
  let cliffPitch: number | null = null;
  const win = 10;
  for (let i = win; i <= total - win; i++) {
    const w = pitches.slice(i, i + win);
    const rate = pct(w.reduce((a, b) => a + b, 0), w.length);
    if (earlyStrikePct - rate >= 0.15) { cliffPitch = i; break; }
  }
  return { earlyStrikePct, lateStrikePct, dropPct, cliffPitch, detected: dropPct >= 0.12 };
}

function buildByInning(ordered: AtBatDoc[]): PitcherByInning[] {
  const map = new Map<number, { pitches: number; strikes: number; walks: number; runs: number }>();
  for (const ab of ordered) {
    const cur = map.get(ab.inning) ?? { pitches: 0, strikes: 0, walks: 0, runs: 0 };
    cur.pitches += ab.pitchCount || ab.pitchSequence.length;
    cur.strikes += ab.strikes ?? 0;
    if (isWalk(ab) || isHbp(ab)) cur.walks++;
    cur.runs += ab.runnersScored || 0;
    map.set(ab.inning, cur);
  }
  return [...map.entries()]
    .sort((a, b) => a[0] - b[0])
    .map(([inning, v]) => ({ inning, pitches: v.pitches, strikePct: pct(v.strikes, v.pitches), walks: v.walks, runs: v.runs }));
}

/** Per-batter signals for OUR batters only (batterTeam === 'us'). */
export function computeBatterSignals(atBats: AtBatDoc[]): BatterGameSignal[] {
  const ours = atBats.filter(ab => ab.batterTeam === 'us' && ab.confidence !== 'low');
  const groups = new Map<string, AtBatDoc[]>();
  for (const ab of ours) {
    const k = keyOf(ab.batterId, ab.batterName);
    (groups.get(k) ?? groups.set(k, []).get(k)!).push(ab);
  }

  const result: BatterGameSignal[] = [];
  for (const pas of groups.values()) {
    const pa = pas.length;
    const abs = pas.filter(isAB);
    const hits = pas.filter(isHit).length;
    const reached = pas.filter(ab => ab.reachedBase).length;
    const rbi = pas.reduce((s, ab) => s + (ab.rbi || 0), 0);
    const walks = pas.filter(isWalk).length;
    const strikeouts = pas.filter(isK).length;
    const pitchesSeen = pas.reduce((s, ab) => s + (ab.pitchCount || ab.pitchSequence.length), 0);

    const standout = hits >= 2 || rbi >= 2 || walks >= 2;
    result.push({
      batterId: pas[0].batterId,
      batterName: pas[0].batterName || 'Unknown',
      pa,
      ab: abs.length,
      hits,
      reached,
      rbi,
      walks,
      strikeouts,
      pitchesSeen,
      pitchesPerPa: pct(pitchesSeen, pa),
      standout,
      line: formatBatterLine(hits, abs.length, walks, rbi),
    });
  }
  return result;
}

function formatBatterLine(hits: number, ab: number, walks: number, rbi: number): string {
  const parts = [`${hits}-for-${ab}`];
  if (walks > 0) parts.push(`${walks} BB`);
  if (rbi > 0) parts.push(`${rbi} RBI`);
  return parts.join(', ');
}

/** Top-level entry: compute all Sprint-1 signals for one game's at-bats.
 *  `exactPitcherLines` is reconcileGameImport's `pitcherLines` — the Game Stats
 *  Sheet's exact per-pitcher totals, when a box score was uploaded. Pass it
 *  through so pitching signals report GameChanger's own numbers instead of
 *  re-deriving (and potentially misattributing) them from the scorebook alone. */
export function computeGameSignals(
  atBats: AtBatDoc[],
  exactPitcherLines: Record<string, StatsSheetPitcher> = {},
  pitcherRates: Record<string, PitcherRates> = {},
): GameSignals {
  return {
    ourPitchers: computePitcherSignals(atBats, exactPitcherLines, pitcherRates),
    ourBatters: computeBatterSignals(atBats),
    lowConfidenceCount: atBats.filter(ab => ab.confidence === 'low').length,
  };
}
